import { message } from 'antd'
import axios from 'axios'

const apiClient = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL_API}`,
})

const API_LOG_PREFIX = '[1JOURNEY_API]'
const SENSITIVE_LOG_KEY = /authorization|cookie|password|token|secret|api[-_]?key/i
let requestSequence = 0
let fetchDiagnosticsInstalled = false
let apiClientResponseInterceptorInstalled = false

const redactLogValue = (value, key = '', seen = new WeakSet()) => {
  if (SENSITIVE_LOG_KEY.test(key)) return '[REDACTED]'
  if (value === null || value === undefined) return value
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const formData = {}
    value.forEach((entryValue, entryKey) => {
      const safeValue = redactLogValue(entryValue, entryKey, seen)
      if (Object.prototype.hasOwnProperty.call(formData, entryKey)) {
        formData[entryKey] = Array.isArray(formData[entryKey])
          ? [...formData[entryKey], safeValue]
          : [formData[entryKey], safeValue]
      } else {
        formData[entryKey] = safeValue
      }
    })
    return { type: 'FormData', fields: formData }
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return { type: 'File', name: value.name, size: value.size, mimeType: value.type }
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return { type: 'Blob', size: value.size, mimeType: value.type }
  }

  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
    const params = {}
    value.forEach((entryValue, entryKey) => {
      params[entryKey] = redactLogValue(entryValue, entryKey, seen)
    })
    return { type: 'URLSearchParams', values: params }
  }

  if (typeof value === 'object') {
    if (seen.has(value)) return '[Circular]'
    seen.add(value)

    if (Array.isArray(value)) {
      return value.map((entry) => redactLogValue(entry, key, seen))
    }

    return Object.entries(value).reduce((result, [entryKey, entryValue]) => {
      result[entryKey] = redactLogValue(entryValue, entryKey, seen)
      return result
    }, {})
  }

  return String(value)
}

const getRequestUrl = (config) => {
  if (!config) return undefined
  const baseURL = config.baseURL || ''
  const requestUrl = config.url || ''
  if (/^https?:\/\//i.test(requestUrl) || !baseURL) return requestUrl || baseURL
  return `${baseURL.replace(/\/$/, '')}/${requestUrl.replace(/^\//, '')}`
}

const getRequestDetails = (config) => ({
  method: (config?.method || 'get').toUpperCase(),
  url: getRequestUrl(config),
  params: redactLogValue(config?.params),
  body: redactLogValue(config?.data),
  headers: redactLogValue(config?.headers),
})

const startApiLog = (details) => {
  const requestId = ++requestSequence
  const log = { requestId, startedAt: new Date().toISOString(), ...details }
  console.log(`${API_LOG_PREFIX}_BEFORE_REQUEST`, log)
  return { requestId, startedAt: Date.now(), details }
}

const logAxiosRequest = (config) => {
  if (!config.__1journeyApiLog) {
    config.__1journeyApiLog = startApiLog(getRequestDetails(config))
  }
  return config
}

const logAxiosError = (error) => {
  const config = error?.config
  const requestLog = config?.__1journeyApiLog
  const details = requestLog?.details || getRequestDetails(config)
  const responseData = error?.response?.data

  console.log(`${API_LOG_PREFIX}_AFTER_ERROR`, {
    requestId: requestLog?.requestId,
    durationMs: requestLog ? Date.now() - requestLog.startedAt : undefined,
    ...details,
    error: {
      message: error?.message,
      code: error?.code,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      response: redactLogValue(responseData),
    },
  })
}

const isApiFetchUrl = (url) => {
  const apiBaseUrl = process.env.REACT_APP_BASE_URL_API
  return !/^https?:\/\//i.test(url) || !apiBaseUrl || url.startsWith(apiBaseUrl)
}

const getFetchRequestDetails = (input, init = {}) => {
  const requestUrl = typeof input === 'string' ? input : input?.url || String(input)
  const requestMethod = init.method || input?.method || 'GET'
  const requestHeaders = init.headers || input?.headers
  const requestBody = init.body

  return {
    method: requestMethod.toUpperCase(),
    url: requestUrl,
    params: undefined,
    body: redactLogValue(requestBody),
    headers: redactLogValue(requestHeaders),
  }
}

const installFetchDiagnostics = () => {
  if (fetchDiagnosticsInstalled || typeof window === 'undefined' || !window.fetch) return

  const originalFetch = window.fetch.bind(window)
  window.fetch = async (input, init = {}) => {
    const details = getFetchRequestDetails(input, init)
    if (!isApiFetchUrl(details.url)) return originalFetch(input, init)

    const requestLog = startApiLog(details)
    try {
      const response = await originalFetch(input, init)
      if (!response.ok) {
        let responseData
        try {
          const responseText = await response.clone().text()
          responseData = responseText ? JSON.parse(responseText) : responseText
        } catch (readError) {
          responseData = `[Unable to read error response: ${readError.message}]`
        }

        console.log(`${API_LOG_PREFIX}_AFTER_ERROR`, {
          requestId: requestLog.requestId,
          durationMs: Date.now() - requestLog.startedAt,
          ...details,
          error: {
            status: response.status,
            statusText: response.statusText,
            response: redactLogValue(responseData),
          },
        })
      }
      return response
    } catch (error) {
      console.log(`${API_LOG_PREFIX}_AFTER_ERROR`, {
        requestId: requestLog.requestId,
        durationMs: Date.now() - requestLog.startedAt,
        ...details,
        error: { message: error.message, name: error.name },
      })
      throw error
    }
  }

  fetchDiagnosticsInstalled = true
}

export const getErrorMessage = (data, fallback = 'Something went wrong') => {
  if (!data) return fallback
  if (typeof data === 'string') return data

  if (typeof data === 'object') {
    // Per-field validation errors are always useful to show.
    if (data.errors && typeof data.errors === 'object') {
      const flattened = Object.values(data.errors).flat().filter(Boolean).join(' ')
      if (flattened) return flattened
    }

    // api-stage contract: only surface the backend message when it is flagged friendly;
    // otherwise fall back to a generic (never show technical details to the user).
    if (data.isFriendly !== false) {
      if (typeof data.title === 'string' && data.title.trim()) {
        return data.title
      }
      if (typeof data.message === 'string' && data.message.trim()) {
        return data.message
      }
    }
  }

  return fallback
}

apiClient.interceptors.request.use(
  (config) => {
    logAxiosRequest(config)
    const token = localStorage.getItem('token')
    const currentUrl = window.location.pathname

    // Stop loader while the shared login page is loading.
    if (currentUrl === '/login') {
      if (config.setLoading) {
        config.setLoading(false)
      }
      return config
    }

    if (!token) {
      // No token yet (e.g. public register/verify flows). Let the request go out
      // without an Authorization header rather than dropping the config.
      return config
    }

    // Attach the bearer token so callers that rely on the shared client (instead of
    // passing an explicit Authorization header) are still authenticated. Without this,
    // such calls go out token-less and 401 on endpoints that require auth.
    config.headers = config.headers || {}
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Start the loader when a request is made
    if (config && config.setLoading) {
      config.setLoading(true)
    }
    return config
  },
  (error) => {
    // Stop the loader if there's an error during the request
    if (error.config && error.config.setLoading) {
      error.config.setLoading(false)
    }
    return Promise.reject(error)
  },
)

export const setupInterceptors = (navigate, setLoading) => {
  installFetchDiagnostics()

  if (apiClientResponseInterceptorInstalled) return
  apiClientResponseInterceptorInstalled = true

  apiClient.interceptors.response.use(
    (response) => {
      // Stop the loader when the response is successful
      if (response.config && response.config.setLoading) {
        response.config.setLoading(false)
      }
      return response
    },
    (error) => {
      logAxiosError(error)

      // Stop the loader when there's an error in the response
      if (error.config && error.config.setLoading) {
        error.config.setLoading(false)
      }

      // api-stage returns a structured error object; flatten it to a message string so
      // that any caller reading error.response.data directly keeps getting a string.
      if (error.response && error.response.data && typeof error.response.data === 'object') {
        error.response.rawData = error.response.data
        error.response.data = getErrorMessage(error.response.data)
      }

      // A failed sign-in must surface a readable error on the login screen itself.
      // Skip the global handler below (its 401 branch clears storage and hard-redirects
      // to /login, which wipes the error message before the user can read it). The login
      // hook shows a clear "incorrect email or password" toast instead.
      if ((error.config?.url || '').includes('/Account/Login')) {
        error.handled = true
        return Promise.reject(error)
      }

      if (error.handled) {
        return Promise.reject(error)
      }

      // Let a caller opt out of the global error toast for an expected/handled failure
      // (e.g. an optional endpoint that may 404), while still rejecting so it can react.
      if (error.config && error.config.suppressErrorToast) {
        error.handled = true
        return Promise.reject(error)
      }

      const errorData = error.response?.data

      if (!error.response) {
        message.error('Network error! Please try again later.')
      } else if (error.response.status === 400) {
        message.error(getErrorMessage(errorData, 'Bad request'))
      } else if (error.response.status === 401) {
        message.error(getErrorMessage(errorData, 'Session expired. Redirecting to login...'))
        localStorage.clear()
        // Hard-redirect so this works even when the caller wired setupInterceptors
        // without a usable navigate (App.js passes setLoading in the navigate slot).
        setTimeout(() => {
          window.location.href = '/login'
        }, 100)
      } else if (error.response.status === 403) {
        message.error(getErrorMessage(errorData, 'Access Denied'))
      } else if (error.response.status === 404) {
        message.error(getErrorMessage(errorData, 'Resource Not found'))
      } else if (error.response.status === 500) {
        message.error(getErrorMessage(errorData, 'Something went wrong! Please try again later'))
      } else {
        message.error(getErrorMessage(errorData, 'Something went wrong'))
      }
      error.handled = true

      return Promise.reject(error)
    },
  )
}

// Some screens call the shared `axios` instance directly instead of apiClient. Normalize the
// api-stage structured error body to a message string for those too, so error.response.data
// stays a displayable string (matching the old api-dev behaviour) after the backend merge.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    logAxiosError(error)

    if (error.response && error.response.data && typeof error.response.data === 'object') {
      error.response.rawData = error.response.data
      error.response.data = getErrorMessage(error.response.data)
    }
    return Promise.reject(error)
  },
)

axios.interceptors.request.use((config) => logAxiosRequest(config))

export default apiClient

// import { message } from 'antd'
// import axios from 'axios'

// const apiClient = axios.create({
//   baseURL: `${process.env.REACT_APP_BASE_URL_API}`,
// })

// apiClient.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem('token')
//     if (!token) {
//       window.location.href = '/login'
//       return Promise.reject(new Error('Unauthorized: No token in local storage'))
//     }
//     return config
//   },
//   (error) => Promise.reject(error),
// )

// export const setupInterceptors = (navigate, setLoading) => {
//   apiClient.interceptors.response.use(
//     (response) => response,
//     (error) => {
//       if (!error.response) {
//         navigate('/login')
//         localStorage.clear()
//         return Promise.reject(error)
//       }
//       if (error.handled) {
//         return Promise.reject(error)
//       }
//       if (error.response.status === 400) {
//         message.error(error.response.data)
//       } else if (error.response.status === 401) {
//         message.error(error.response.data)
//         localStorage.clear()
//         setTimeout(() => navigate('/login'), 100)
//       } else if (error.response.status === 403) {
//         message.error('Access Denied')
//       } else if (error.response.status === 404) {
//         message.error(error.response.data || 'Resource Not found')
//       } else {
//         message.error('Something went wrong')
//       }
//       error.handled = true

//       return Promise.reject(error)
//     },
//   )
// }

// export default apiClient
